import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import {
  Box,
  Button,
  TextField,
  useMediaQuery,
  Typography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { theme } from "../../../../components/special/genie-theme/genie-theme";
import { Address } from "../../../../types/api/Address";
import NumberFormat from "react-number-format";
import useUpdateAddress from "../../../../queries/routes/useUpdateAddress";
import useCreatePackageTemplate from "../../../../queries/routes/useCreatePackageTemplate";
import usePackageTemplates from "../../../../queries/routes/usePackageTemplates";
import { useEffect } from "react";
import useUser from "../../../../queries/routes/useUser";
import { Organization } from "../../../../types/api/User";
import { Undefinable } from "../../../../utils/Nullable";
import MenuItem from "@mui/material/MenuItem";
import GenieText from "../../../../components/core/genie-text/GenieText";

interface AddressDialogProps {
  open: boolean;
  setClosed: () => void;
}

interface ValidationErrors {
  height: boolean;
  width: boolean;
  length: boolean;
  dimensionUnit: boolean;
  line1: boolean;
  city: boolean;
  state: boolean;
  postal_code: boolean;
}

const defaultAddress: Address = {
  city: "",
  country: "",
  line1: "",
  line2: "",
  postal_code: "",
  state: "",
};

const dimensionUnits = ["ft", "in", "cm", "m"];

export default function AddressDialog({ open, setClosed }: AddressDialogProps) {
  const { org_id } = useParams();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { mutate: updateAddress, isSuccess: isAddressSuccess } =
    useUpdateAddress(org_id!);
  const { mutate: createPackageTemplate, isSuccess: isPackageSuccess } =
    useCreatePackageTemplate(org_id!);
  const { data: packageTemplates } = usePackageTemplates(org_id!);
  const { data: user } = useUser();
  const [address, setAddress] = React.useState<Address>(defaultAddress);

  // Local state for package dimensions
  const [packageDimensions, setPackageDimensions] = React.useState({
    height: "",
    width: "",
    length: "",
    dimensionUnit: "",
  });

  const [errors, setErrors] = React.useState<ValidationErrors>({
    height: false,
    width: false,
    length: false,
    dimensionUnit: false,
    line1: false,
    city: false,
    state: false,
    postal_code: false,
  });

  let organization: Undefinable<Organization>;
  if (user && user.organizations) {
    organization = user?.organizations.find((org) => org.id === org_id);
  }

  useEffect(() => {
    if (isAddressSuccess && isPackageSuccess) {
      setClosed();
    }
  }, [isAddressSuccess, isPackageSuccess]);

  useEffect(() => {
    if (organization && organization.address) {
      setAddress(organization.address);
    }
  }, [organization]);

  // Add effect to load default package template dimensions
  useEffect(() => {
    if (packageTemplates && packageTemplates.length > 0) {
      // Use the first package template as the default
      const defaultTemplate = packageTemplates[0];
      setPackageDimensions({
        height: defaultTemplate.height.toString(),
        width: defaultTemplate.width.toString(),
        length: defaultTemplate.length.toString(),
        dimensionUnit: defaultTemplate.dimension_unit,
      });
    }
  }, [packageTemplates]);

  const handleClose = () => {
    setClosed();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setAddress({
      ...address,
      [name]: value,
    });
  };

  const handlePackageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setPackageDimensions({
      ...packageDimensions,
      [name]: value,
    });
  };

  const handlePackageSelectChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const name = event.target.name as string;
    setPackageDimensions({
      ...packageDimensions,
      [name]: event.target.value as string,
    });
  };

  const validateForm = (): boolean => {
    const newErrors = {
      height: !packageDimensions.height,
      width: !packageDimensions.width,
      length: !packageDimensions.length,
      dimensionUnit: !packageDimensions.dimensionUnit,
      line1: !address.line1,
      city: !address.city,
      state: !address.state,
      postal_code: !address.postal_code,
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const submit = () => {
    if (!validateForm()) {
      return;
    }

    // Submit address
    updateAddress(address);

    // Submit package template if dimensions are provided
    if (
      packageDimensions.height &&
      packageDimensions.width &&
      packageDimensions.length &&
      packageDimensions.dimensionUnit
    ) {
      createPackageTemplate({
        name: "Default Package",
        height: parseFloat(packageDimensions.height),
        width: parseFloat(packageDimensions.width),
        length: parseFloat(packageDimensions.length),
        dimension_unit: packageDimensions.dimensionUnit,
        organization_id: org_id!,
      });
    }
  };

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={handleClose}>
      <DialogContent>
        <GenieText
          fontSize="medium"
          fontWeight="normal"
          style={{ marginBottom: "16px" }}
        >
          Add Package Dimensions
        </GenieText>
        <DialogContentText sx={{ mb: 2 }}>
          Default for the package you use most often
        </DialogContentText>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <TextField
            label="H*"
            name="height"
            value={packageDimensions.height}
            onChange={handlePackageInputChange}
            type="number"
            size="small"
            sx={{ width: 80 }}
            error={errors.height}
          />
          <span>x</span>
          <TextField
            label="W*"
            name="width"
            value={packageDimensions.width}
            onChange={handlePackageInputChange}
            type="number"
            size="small"
            sx={{ width: 80 }}
            error={errors.width}
          />
          <span>x</span>
          <TextField
            label="L*"
            name="length"
            value={packageDimensions.length}
            onChange={handlePackageInputChange}
            type="number"
            size="small"
            sx={{ width: 80 }}
            error={errors.length}
          />
          <TextField
            select
            label="Unit*"
            name="dimensionUnit"
            value={packageDimensions.dimensionUnit}
            onChange={handlePackageSelectChange}
            size="small"
            sx={{ width: 70 }}
            error={errors.dimensionUnit}
          >
            {dimensionUnits.map((unit) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <GenieText
          fontSize="medium"
          fontWeight="normal"
          style={{ marginTop: "24px", marginBottom: "16px" }}
        >
          Add Shipping Address
        </GenieText>
        <DialogContentText sx={{ mb: 1 }}>
          Add the business address that you will be shipping from
        </DialogContentText>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          mb={2}
        >
          <Box
            component={"form"}
            mt={2}
            width={`100%`}
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gap={2}
          >
            <Box gridColumn="span 12">
              <TextField
                id="addressLine1"
                label="Address"
                variant="outlined"
                required
                fullWidth
                name="line1"
                value={address.line1}
                onChange={handleInputChange}
                error={errors.line1}
              />
            </Box>
            <Box gridColumn="span 12">
              <TextField
                id="addressLine2"
                label="Apt / Floor / Suite (optional)"
                variant="outlined"
                fullWidth
                name="line2"
                value={address.line2}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={"span 4"}>
              <TextField
                id="city"
                label="City"
                variant="outlined"
                fullWidth
                name="city"
                value={address.city}
                required
                onChange={handleInputChange}
                error={errors.city}
              />
            </Box>
            <Box gridColumn={"span 4"}>
              <TextField
                id="state"
                label="State"
                variant="outlined"
                fullWidth
                name="state"
                value={address.state}
                required
                onChange={handleInputChange}
                error={errors.state}
              />
            </Box>
            <Box gridColumn={"span 4"}>
              <NumberFormat
                customInput={TextField}
                id="postal_code"
                label="Postal Code"
                variant="outlined"
                fullWidth
                name="postal_code"
                value={address.postal_code}
                format="#####"
                mask="_"
                required
                onChange={handleInputChange}
                error={errors.postal_code}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button disabled={false} onClick={submit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
